import firebase from 'firebase/app';

import { startSession, stopSession, isExtensionInstalled } from '@/services/extension';
import { successAction } from '@/store/actionTypes';
import { SESSION_FINISHED, SESSION_STARTED } from '@/store/session/actionTypes';
import { SAVE_PRIVATE_INFO } from '@/store/privateInfo/actionTypes';
import { showInstallExtensionDialog, showExtensionInstalledDialog } from '@/services/dialogs';
import { isSupportedBrowser, isDesktop } from '@/services/browser';
import router from '@/router';
import check from '@/services/features';
import { getExtensionDataForPrivateInfo } from '@/services/privateInfo';
import logger from '@/services/logger';
import { UPDATE_EXTENSION_STATUS } from './actionTypes';
import { SET_EXTENSION_INSTALLATION_STATS } from './mutationTypes';

function notifyExtensionAboutSessionUpdating() {
  const user = firebase.auth().currentUser;

  logger.log({
    type: 'NOTIFY_EXTENSION',
    message: user ? 'authorized' : 'logged out',
  });

  if (user) {
    startSession(user);
  } else {
    stopSession();
  }
}

function extIsRequiredForRoute(route) {
  return route && route?.meta?.extension;
}

function isCurrentRouteRequireExt() {
  return (
    extIsRequiredForRoute(router.currentRoute) ??
    router.currentRoute.matched.some(extIsRequiredForRoute)
  );
}

function isCurrentRouteSupportExtInstalledPopup() {
  const { action, target } = router.currentRoute.query;
  return action && action === 'installed' && target === 'extension';
}

function removeExtInstalledParamsFromQuery() {
  const { action, target, ...query } = router.currentRoute.query;
  router.replace({ path: router.currentRoute.path, query });
}

async function onExtensionNotInstalled() {
  const enabled = await check.feature.EXTENSION_NOT_INSTALLED_POPUP;
  if (!enabled) {
    return;
  }

  // wait for possible redirect and then check
  setTimeout(() => {
    if (isCurrentRouteRequireExt() && isSupportedBrowser() && isDesktop()) {
      showInstallExtensionDialog();
    }
  });
}

function onExtensionInstalled() {
  // wait for possible redirect and then check
  setTimeout(() => {
    if (isCurrentRouteSupportExtInstalledPopup()) {
      showExtensionInstalledDialog();
      removeExtInstalledParamsFromQuery();
    }
  });
}

export default {
  async [UPDATE_EXTENSION_STATUS]({ getters, commit, dispatch }, { force = false } = {}) {
    const installed = await isExtensionInstalled();
    const installationFlagChanged = getters.extensionInstalled !== installed;

    if (installationFlagChanged || force) {
      if (installed) {
        notifyExtensionAboutSessionUpdating();
        onExtensionInstalled();

        const shouldSaveExtensionData =
          !getters.extensionInstalled && !getters.privateInfoLoading && firebase.auth().currentUser;

        if (shouldSaveExtensionData) {
          dispatch(SAVE_PRIVATE_INFO, await getExtensionDataForPrivateInfo());
        }
      } else {
        onExtensionNotInstalled();
      }

      commit(SET_EXTENSION_INSTALLATION_STATS, installed);
    }
  },
  [successAction(SESSION_STARTED)]({ dispatch }) {
    dispatch(UPDATE_EXTENSION_STATUS, { force: true });
  },
  [successAction(SESSION_FINISHED)]({ dispatch }) {
    dispatch(UPDATE_EXTENSION_STATUS, { force: true });
  },
};
